import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { informer } from '@/components/ui/Fenetres';
import { Edit, Trash2, KeyRound, RefreshCw, Users, AlertTriangle, Check, X, Copy } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useHoldingUsers, useUpdateHoldingUser, useDeleteHoldingUser, useResetHoldingUserPassword,
} from '@/hooks/useHoldingUsers';
import { type HoldingUserRow, type UpdateHoldingUserPayload } from '@/api/holding-users';
import { extractError } from '@/api/client';

const ROLE_LABELS: Record<string, string> = {
  admin_holding: 'Admin Holding',
  auditeur:      'Auditeur',
  dg_filiale:    'DG Filiale',
  daf:           'DAF',
  manager:       'Manager',
  rh:            'RH',
  acheteur:      'Acheteur',
  commercial:    'Commercial',
  chef_equipe:   'Chef d\'équipe',
};

const fmtDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
const fmtDT   = (iso: string | null) => iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

export const UsersAdminPage = () => {
  const [filiale, setFiliale] = useState<string>('');
  const [search, setSearch]   = useState('');
  const [editFor, setEditFor]   = useState<HoldingUserRow | null>(null);
  const [deleteFor, setDeleteFor] = useState<HoldingUserRow | null>(null);
  const [resetFor, setResetFor]   = useState<HoldingUserRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: users = [], isLoading, refetch } = useHoldingUsers(filiale || undefined);

  // Liste filiales disponibles depuis les rows
  const filialesOptions = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u) => { if (u.filialeCode) map.set(u.filialeCode, u.filialeName ?? u.filialeCode); });
    return [...map.entries()].map(([code, name]) => ({ code, name }));
  }, [users]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const s = search.trim().toLowerCase();
    return users.filter((u) =>
      u.email.toLowerCase().includes(s) ||
      u.fullName.toLowerCase().includes(s) ||
      u.role.toLowerCase().includes(s) ||
      (u.filialeName ?? '').toLowerCase().includes(s),
    );
  }, [users, search]);

  return (
    <>
      <Topbar
        title="Utilisateurs · Holding"
        subtitle="Administration cross-tenant — CTO-ACCESS-001 (admin_holding uniquement)"
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        }
      />
      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        <div className="ds-kpi-strip">
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Comptes utilisateurs</div>
            <div className="ds-ks-value">{users.length}</div>
            <div className="ds-ks-sub">Sur {new Set(users.map((u) => u.filialeCode)).size} tenant{new Set(users.map((u) => u.filialeCode)).size > 1 ? 's' : ''} actif{new Set(users.map((u) => u.filialeCode)).size > 1 ? 's' : ''}</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Actifs</div>
            <div className="ds-ks-value">{users.filter((u) => u.isActive).length}</div>
            <div className="ds-ks-sub">Peuvent se connecter</div>
          </div>
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Désactivés</div>
            <div className="ds-ks-value">{users.filter((u) => !u.isActive).length}</div>
            <div className="ds-ks-sub">Compte suspendu</div>
          </div>
          <div className="ds-ks ks-purple">
            <div className="ds-ks-label">Admin Holding</div>
            <div className="ds-ks-value">{users.filter((u) => u.role === 'admin_holding').length}</div>
            <div className="ds-ks-sub">Accès complet</div>
          </div>
        </div>

        <div className="ds-filters" style={{ gap: 12 }}>
          <select className="ds-fi" value={filiale} onChange={(e) => setFiliale(e.target.value)} style={{ width: 'auto', minWidth: 200 }}>
            <option value="">Toutes les filiales</option>
            {filialesOptions.map((f) => (
              <option key={f.code} value={f.code}>{f.name}</option>
            ))}
          </select>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher email, nom, rôle, filiale…"
            className="ds-fi"
            style={{ minWidth: 280 }}
          />
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Utilisateurs ({filtered.length})</div>
              <div className="ds-table-sub">Cross-tenant via API Admin Supabase de chaque projet</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th>Email</th>
                <th>Nom</th>
                <th style={{ width: 130 }}>Rôle</th>
                <th style={{ width: 160 }}>Filiale</th>
                <th style={{ width: 100 }}>Statut</th>
                <th style={{ width: 100 }}>Créé le</th>
                <th style={{ width: 120 }}>Dernière conn.</th>
                <th style={{ width: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}>Chargement multi-tenant…</td></tr>}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  <Users size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun utilisateur</p>
                </td></tr>
              )}
              {!isLoading && filtered.map((u) => (
                <tr key={u.id} style={{ opacity: u.isActive ? 1 : 0.55 }}>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{u.email}</td>
                  <td>{u.fullName}</td>
                  <td>
                    <span className="ds-bd ds-bd-i" style={{ fontSize: 10 }}>{ROLE_LABELS[u.role] ?? u.role}</span>
                  </td>
                  <td style={{ fontSize: 11 }}>
                    <div className="ds-entry-label" style={{ fontSize: 12 }}>{u.filialeName ?? u.filialeCode}</div>
                    <div style={{ color: 'var(--tx-3)', fontSize: 10 }}>{u.filialeCode}</div>
                  </td>
                  <td>
                    {u.isActive
                      ? <span className="ds-bd ds-bd-s" style={{ fontSize: 10 }}>✓ Actif</span>
                      : <span className="ds-bd ds-bd-d" style={{ fontSize: 10 }}>✗ Désactivé</span>}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>{fmtDate(u.createdAt)}</td>
                  <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{fmtDT(u.lastLoginAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setEditFor(u)} title="Modifier">
                        <Edit size={11} />
                      </button>
                      <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setResetFor(u)} title="Reset password" style={{ color: 'var(--info)' }}>
                        <KeyRound size={11} />
                      </button>
                      <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setDeleteFor(u)} title="Supprimer" style={{ color: 'var(--danger)' }}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <EditUserDialog user={editFor} onClose={() => setEditFor(null)} onError={setActionError} />
      <DeleteUserDialog user={deleteFor} onClose={() => setDeleteFor(null)} onError={setActionError} />
      <ResetPasswordDialog user={resetFor} onClose={() => setResetFor(null)} onError={setActionError} />
    </>
  );
};

// ── Dialog Edit ────────────────────────────────────────

const EditUserDialog = ({ user, onClose, onError }: { user: HoldingUserRow | null; onClose: () => void; onError: (m: string | null) => void }) => {
  const open = user !== null;
  const update = useUpdateHoldingUser();
  const [role, setRole] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) {
      setRole(user.role); setFullName(user.fullName);
      setPhone(user.phone ?? ''); setIsActive(user.isActive);
      setError(null);
    }
  }, [open, user]);

  if (!user) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); onError(null);
    const payload: UpdateHoldingUserPayload = {};
    if (role !== user.role) payload.role = role;
    if (fullName !== user.fullName) payload.fullName = fullName.trim();
    if (phone !== (user.phone ?? '')) payload.phone = phone.trim() || null;
    if (isActive !== user.isActive) payload.isActive = isActive;
    if (Object.keys(payload).length === 0) { onClose(); return; }
    try {
      await update.mutateAsync({ userId: user.id, filiale: user.filialeCode, payload });
      onClose();
    } catch (err) { const m = extractError(err).message; setError(m); onError(m); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Modifier ${user.email}`} subtitle={`${user.filialeName ?? user.filialeCode}`} size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <Field label="Nom complet">
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="ds-fi" style={{ width: '100%' }} required />
        </Field>

        <Field label="Rôle">
          <select value={role} onChange={(e) => setRole(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
            {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>

        <Field label="Téléphone">
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="+225..." />
        </Field>

        <Field label="Statut compte">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span>{isActive ? 'Actif (peut se connecter)' : 'Désactivé (connexion bloquée)'}</span>
          </label>
        </Field>

        <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
          Email : <strong>{user.email}</strong> · Filiale : <strong>{user.filialeName ?? user.filialeCode}</strong> · Ne sont pas modifiables ici.
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={update.isPending}>
            <Check size={14} /> {update.isPending ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Dialog Delete ──────────────────────────────────────

const DeleteUserDialog = ({ user, onClose, onError }: { user: HoldingUserRow | null; onClose: () => void; onError: (m: string | null) => void }) => {
  const open = user !== null;
  const remove = useDeleteHoldingUser();
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (open) { setConfirm(''); setError(null); } }, [open]);

  if (!user) return null;

  const canConfirm = confirm === user.email;

  const onSubmit = async () => {
    setError(null); onError(null);
    try {
      await remove.mutateAsync({ userId: user.id, filiale: user.filialeCode });
      onClose();
    } catch (err) { const m = extractError(err).message; setError(m); onError(m); }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Supprimer un utilisateur" subtitle="Action IRRÉVERSIBLE" size="sm">
      <div className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          <strong>⚠ Cette action supprime définitivement :</strong>
          <ul className="mt-2 ml-4 list-disc space-y-1">
            <li>Le compte Supabase Auth (auth.users)</li>
            <li>Le profil application (public.users) dans <strong>{user.filialeName ?? user.filialeCode}</strong></li>
            <li>L'utilisateur ne pourra plus se connecter</li>
          </ul>
        </div>

        <div>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            Email : <strong style={{ fontFamily: 'monospace' }}>{user.email}</strong>
          </div>
          <div style={{ fontSize: 13, marginBottom: 6 }}>Nom : <strong>{user.fullName}</strong></div>
          <div style={{ fontSize: 13 }}>Rôle : <strong>{ROLE_LABELS[user.role] ?? user.role}</strong></div>
        </div>

        <Field label="Pour confirmer, tapez l'email complet :">
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={user.email}
            className="ds-fi"
            style={{ width: '100%', fontFamily: 'monospace' }}
            autoFocus
          />
        </Field>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button
            type="button"
            className="ds-btn"
            style={{ background: canConfirm ? 'var(--danger)' : '#fca5a5', color: 'white', opacity: canConfirm ? 1 : 0.5, cursor: canConfirm ? 'pointer' : 'not-allowed' }}
            disabled={!canConfirm || remove.isPending}
            onClick={onSubmit}
          >
            <Trash2 size={14} /> {remove.isPending ? 'Suppression…' : 'Confirmer suppression'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

// ── Dialog Reset Password ──────────────────────────────

const ResetPasswordDialog = ({ user, onClose, onError }: { user: HoldingUserRow | null; onClose: () => void; onError: (m: string | null) => void }) => {
  const open = user !== null;
  const reset = useResetHoldingUserPassword();
  const [result, setResult] = useState<{ actionLink: string | null; sent: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (open) { setResult(null); setError(null); } }, [open]);

  if (!user) return null;

  const onSubmit = async () => {
    setError(null); onError(null);
    try {
      const r = await reset.mutateAsync({ userId: user.id, filiale: user.filialeCode });
      setResult({ actionLink: r.actionLink, sent: r.sent });
    } catch (err) { const m = extractError(err).message; setError(m); onError(m); }
  };

  const copyLink = () => {
    if (result?.actionLink) {
      navigator.clipboard.writeText(result.actionLink).then(() => informer('Lien copié dans le presse-papiers'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Réinitialiser mot de passe — ${user.email}`} subtitle="Génère un lien de reset via Supabase Auth" size="md">
      <div className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        {!result && (
          <>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
              Un lien de reset password sera généré via Supabase Auth de la filiale <strong>{user.filialeName ?? user.filialeCode}</strong>.
              <br />Selon la config SMTP du projet Supabase :
              <ul className="mt-2 ml-4 list-disc">
                <li>Si SMTP activé → email envoyé directement à <strong>{user.email}</strong></li>
                <li>Si SMTP non configuré → lien à partager manuellement par l'admin</li>
              </ul>
            </div>

            <div className="flex justify-end gap-2 border-t pt-3">
              <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
              <button type="button" className="ds-btn ds-btn-p" onClick={onSubmit} disabled={reset.isPending}>
                <KeyRound size={14} /> {reset.isPending ? 'Génération…' : 'Générer lien reset'}
              </button>
            </div>
          </>
        )}

        {result && (
          <>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
              <strong>✓ Lien de reset généré</strong>
              {result.sent && <div className="mt-1">Email envoyé via Supabase SMTP à <strong>{user.email}</strong>.</div>}
              {!result.sent && result.actionLink && (
                <div className="mt-2">
                  <div>SMTP non configuré — partagez ce lien manuellement à l'utilisateur :</div>
                  <div style={{ marginTop: 6, padding: 8, background: 'white', borderRadius: 4, border: '1px solid var(--bdr)', fontFamily: 'monospace', fontSize: 10, wordBreak: 'break-all', maxHeight: 120, overflow: 'auto' }}>
                    {result.actionLink}
                  </div>
                  <button type="button" className="ds-btn ds-btn-g ds-btn-sm" onClick={copyLink} style={{ marginTop: 6 }}>
                    <Copy size={11} /> Copier le lien
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t pt-3">
              <button type="button" className="ds-btn ds-btn-p" onClick={onClose}><X size={14} /> Fermer</button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
